import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  deleteDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { defaultFsrsParams, defaultSrs } from "../../srs/fsrs";

const DECKS = "decks";
const CARDS = "cards";
const SESSIONS = "sessions";
const REVIEWS = "reviews";
const STREAKS = "srs_streaks";

function requireUid() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("You must be signed in.");
  return uid;
}

function toTs(date) {
  if (date instanceof Timestamp) return date;
  if (date?.toDate) return date;
  return Timestamp.fromDate(date instanceof Date ? date : new Date(date));
}

function localDateKey(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function previousDateKey(dateKey) {
  const [y, m, d] = String(dateKey).split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() - 1);
  return localDateKey(dt);
}

export async function listDecks() {
  const uid = requireUid();
  const q = query(
    collection(db, DECKS),
    where("ownerUid", "==", uid),
    orderBy("updatedAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getSrsStreak() {
  const uid = requireUid();
  const ref = doc(db, STREAKS, uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return {
      streakCount: 0,
      longestStreak: 0,
      lastStudyDate: null,
    };
  }
  const data = snap.data();
  return {
    streakCount: Number(data.streakCount || 0),
    longestStreak: Number(data.longestStreak || 0),
    lastStudyDate: data.lastStudyDate || null,
  };
}

export async function markDailyStudyActivity(date = new Date()) {
  const uid = requireUid();
  const todayKey = localDateKey(date);
  const yesterdayKey = previousDateKey(todayKey);
  const ref = doc(db, STREAKS, uid);
  const nowTs = Timestamp.now();

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists() ? snap.data() : null;
    const prevLast = current?.lastStudyDate || null;
    const prevStreak = Number(current?.streakCount || 0);
    const prevLongest = Number(current?.longestStreak || 0);

    if (prevLast === todayKey) {
      return {
        streakCount: prevStreak,
        longestStreak: prevLongest,
        lastStudyDate: prevLast,
        alreadyCountedToday: true,
        keptToday: false,
      };
    }

    const nextStreak = prevLast === yesterdayKey ? prevStreak + 1 : 1;
    const nextLongest = Math.max(prevLongest, nextStreak);

    tx.set(ref, {
      ownerUid: uid,
      streakCount: nextStreak,
      longestStreak: nextLongest,
      lastStudyDate: todayKey,
      createdAt: current?.createdAt || nowTs,
      updatedAt: nowTs,
    }, { merge: true });

    return {
      streakCount: nextStreak,
      longestStreak: nextLongest,
      lastStudyDate: todayKey,
      alreadyCountedToday: false,
      keptToday: true,
    };
  });
}

export async function createDeck({ name, description = "" }) {
  const uid = requireUid();
  const now = serverTimestamp();
  const payload = {
    ownerUid: uid,
    name: String(name || "").trim(),
    description: String(description || "").trim(),
    fsrsParams: { ...defaultFsrsParams },
    createdAt: now,
    updatedAt: now,
  };
  const ref = await addDoc(collection(db, DECKS), payload);
  return ref.id;
}

export async function getDeck(deckId) {
  const uid = requireUid();
  const ref = doc(db, DECKS, deckId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Deck not found.");
  const data = snap.data();
  if (data.ownerUid !== uid) throw new Error("Deck not accessible.");
  return { id: snap.id, ...data };
}

export async function importCards(deckId, rows) {
  const uid = requireUid();
  const nowDate = new Date();
  const nowTs = Timestamp.fromDate(nowDate);
  const chunkSize = 400;

  for (let i = 0; i < rows.length; i += chunkSize) {
    const batch = writeBatch(db);
    rows.slice(i, i + chunkSize).forEach((row) => {
      const ref = doc(collection(db, CARDS));
      const baseSrs = defaultSrs(nowDate);
      batch.set(ref, {
        ownerUid: uid,
        deckId,
        front: row.front,
        back: row.back,
        tags: row.tags || [],
        strict: !!row.strict,
        accepted: row.accepted || [],
        createdAt: nowTs,
        updatedAt: nowTs,
        srs: {
          ...baseSrs,
          dueAt: nowTs,
          lastReviewedAt: null,
        },
      });
    });
    await batch.commit();
  }

  await updateDoc(doc(db, DECKS, deckId), { updatedAt: serverTimestamp() });
}

export async function getDueCards(deckId, targetCount) {
  const uid = requireUid();
  const nowTs = Timestamp.now();

  const q = query(
    collection(db, CARDS),
    where("ownerUid", "==", uid),
    where("deckId", "==", deckId),
    where("srs.dueAt", "<=", nowTs),
    orderBy("srs.dueAt", "asc"),
    limit(targetCount)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getDueCardsAcrossDecks(targetCount) {
  const uid = requireUid();
  const nowTs = Timestamp.now();

  const q = query(
    collection(db, CARDS),
    where("ownerUid", "==", uid),
    where("srs.dueAt", "<=", nowTs),
    orderBy("srs.dueAt", "asc"),
    limit(targetCount)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getUpcomingCards(deckId, targetCount) {
  const uid = requireUid();
  const q = query(
    collection(db, CARDS),
    where("ownerUid", "==", uid),
    where("deckId", "==", deckId),
    orderBy("srs.dueAt", "asc"),
    limit(targetCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getUpcomingCardsAcrossDecks(targetCount) {
  const uid = requireUid();
  const q = query(
    collection(db, CARDS),
    where("ownerUid", "==", uid),
    orderBy("srs.dueAt", "asc"),
    limit(targetCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getSessionCardsForDeck(deckId, targetCount) {
  const dueCards = await getDueCards(deckId, targetCount);
  if (dueCards.length > 0) return { cards: dueCards, source: "due" };
  const fallback = await getUpcomingCards(deckId, targetCount);
  return { cards: fallback, source: "upcoming" };
}

export async function getSessionCardsAcrossDecks(targetCount) {
  const dueCards = await getDueCardsAcrossDecks(targetCount);
  if (dueCards.length > 0) return { cards: dueCards, source: "due" };
  const fallback = await getUpcomingCardsAcrossDecks(targetCount);
  return { cards: fallback, source: "upcoming" };
}

export async function getDueCardsAcrossDecksCount() {
  const uid = requireUid();
  const nowTs = Timestamp.now();
  const q = query(
    collection(db, CARDS),
    where("ownerUid", "==", uid),
    where("srs.dueAt", "<=", nowTs)
  );
  const snap = await getCountFromServer(q);
  return snap.data().count || 0;
}

export async function createStudySession(deckId, cardIds, targetCount) {
  const uid = requireUid();
  const ref = await addDoc(collection(db, SESSIONS), {
    ownerUid: uid,
    deckId,
    startedAt: serverTimestamp(),
    endedAt: null,
    targetCount,
    cardIds,
    status: "active",
  });
  return ref.id;
}

function normalizeSessionProgress(progress) {
  const safe = progress && typeof progress === "object" ? progress : {};
  const cardUpdates = {};
  Object.entries(safe.cardUpdates || {}).forEach(([cardId, srs]) => {
    if (!cardId || !srs || typeof srs !== "object") return;
    cardUpdates[cardId] = {
      ...srs,
      dueAt: toTs(srs.dueAt || Timestamp.now()),
      lastReviewedAt: srs.lastReviewedAt ? toTs(srs.lastReviewedAt) : null,
    };
  });

  const reviews = Array.isArray(safe.reviews)
    ? safe.reviews.map((review) => ({
      ...review,
      reviewedAt: toTs(review.reviewedAt || Timestamp.now()),
    }))
    : [];

  const sessionQueue = Array.isArray(safe.sessionQueue)
    ? safe.sessionQueue.filter(Boolean).map(String)
    : [];

  const dueSeedIds = Array.isArray(safe.dueSeedIds)
    ? safe.dueSeedIds.filter(Boolean).map(String)
    : [];

  return {
    sessionQueue,
    cursor: Math.max(0, Number(safe.cursor || 0)),
    sessionSource: safe.sessionSource === "upcoming" ? "upcoming" : "due",
    cardUpdates,
    reviews,
    dueSeedIds,
  };
}

export async function saveStudySessionProgress(sessionId, progress) {
  const uid = requireUid();
  const sessionRef = doc(db, SESSIONS, sessionId);
  const snap = await getDoc(sessionRef);
  if (!snap.exists()) throw new Error("Session not found.");
  if (snap.data().ownerUid !== uid) throw new Error("Session not accessible.");
  if (snap.data().status !== "active") return;

  await updateDoc(sessionRef, {
    progress: normalizeSessionProgress(progress),
    updatedAt: serverTimestamp(),
  });
}

export async function getActiveStudySession(deckId) {
  const uid = requireUid();
  const q = query(
    collection(db, SESSIONS),
    where("ownerUid", "==", uid),
    where("deckId", "==", deckId),
    where("status", "==", "active"),
    orderBy("startedAt", "desc"),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data();
  return {
    id: d.id,
    ...data,
    progress: normalizeSessionProgress(data.progress || {}),
  };
}

export async function getCardsByIds(cardIds) {
  const uid = requireUid();
  const ids = Array.isArray(cardIds) ? cardIds.filter(Boolean).map(String) : [];
  if (!ids.length) return [];

  const byId = {};
  const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, CARDS, id))));
  snaps.forEach((snap) => {
    if (!snap.exists()) return;
    const data = snap.data();
    if (data.ownerUid !== uid) return;
    byId[snap.id] = { id: snap.id, ...data };
  });

  return ids.map((id) => byId[id]).filter(Boolean);
}

export async function completeStudySession({ sessionId, deckId, cardUpdates, reviews }) {
  const uid = requireUid();
  const nowTs = Timestamp.now();
  const sessionRef = doc(db, SESSIONS, sessionId);
  const sessionSnap = await getDoc(sessionRef);
  if (!sessionSnap.exists()) throw new Error("Session not found.");
  if (sessionSnap.data().ownerUid !== uid) throw new Error("Session not accessible.");

  const ops = [];
  ops.push({
    type: "update",
    ref: sessionRef,
    data: { endedAt: nowTs, status: "completed" },
  });

  Object.entries(cardUpdates).forEach(([cardId, srs]) => {
    ops.push({
      type: "update",
      ref: doc(db, CARDS, cardId),
      data: {
        srs: {
          ...srs,
          dueAt: toTs(srs.dueAt),
          lastReviewedAt: toTs(srs.lastReviewedAt || nowTs),
        },
        updatedAt: nowTs,
      },
    });
  });

  reviews.forEach((review) => {
    ops.push({
      type: "set",
      ref: doc(collection(db, REVIEWS)),
      data: {
        ownerUid: uid,
        sessionId,
        deckId: review.deckId || deckId,
        cardId: review.cardId,
        reviewedAt: toTs(review.reviewedAt || nowTs),
        grade: review.grade,
        transcript: review.transcript || "",
        correct: typeof review.correct === "boolean" ? review.correct : null,
        rawAnswer: review.rawAnswer || "",
      },
    });
  });

  const chunkSize = 450;
  for (let i = 0; i < ops.length; i += chunkSize) {
    const batch = writeBatch(db);
    ops.slice(i, i + chunkSize).forEach((op) => {
      if (op.type === "update") {
        batch.update(op.ref, op.data);
      } else {
        batch.set(op.ref, op.data);
      }
    });
    await batch.commit();
  }
}

export async function getDeckSummary(deckId) {
  const uid = requireUid();
  const nowTs = Timestamp.now();

  const [dueSnap, totalSnap] = await Promise.all([
    getDocs(query(
      collection(db, CARDS),
      where("ownerUid", "==", uid),
      where("deckId", "==", deckId),
      where("srs.dueAt", "<=", nowTs)
    )),
    getDocs(query(
      collection(db, CARDS),
      where("ownerUid", "==", uid),
      where("deckId", "==", deckId)
    )),
  ]);

  return {
    dueCount: dueSnap.size,
    totalCount: totalSnap.size,
  };
}

export async function listDeckCards(deckId, maxCount = 500) {
  const uid = requireUid();
  const snap = await getDocs(query(
    collection(db, CARDS),
    where("ownerUid", "==", uid),
    where("deckId", "==", deckId),
    limit(Math.max(1, Math.min(1000, Number(maxCount) || 500)))
  ));

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.front || "").localeCompare(String(b.front || "")));
}

export async function updateDeckCard(cardId, patch) {
  const uid = requireUid();
  const ref = doc(db, CARDS, cardId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Card not found.");
  if (snap.data().ownerUid !== uid) throw new Error("Card not accessible.");

  const next = {
    front: String(patch.front || "").trim(),
    back: String(patch.back || "").trim(),
    tags: Array.isArray(patch.tags) ? patch.tags : [],
    strict: !!patch.strict,
    accepted: Array.isArray(patch.accepted) ? patch.accepted : [],
    updatedAt: serverTimestamp(),
  };

  if (!next.front || !next.back) {
    throw new Error("Card front and back are required.");
  }

  await updateDoc(ref, next);
}

export async function getDeckCard(cardId) {
  const uid = requireUid();
  const ref = doc(db, CARDS, cardId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Card not found.");
  const data = snap.data();
  if (data.ownerUid !== uid) throw new Error("Card not accessible.");
  return { id: snap.id, ...data };
}

export async function deleteDeckCard(cardId) {
  const uid = requireUid();
  const ref = doc(db, CARDS, cardId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Card not found.");
  if (snap.data().ownerUid !== uid) throw new Error("Card not accessible.");
  await deleteDoc(ref);
}

export async function updateDeckCardMedia(cardId, patch) {
  const uid = requireUid();
  const ref = doc(db, CARDS, cardId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Card not found.");
  if (snap.data().ownerUid !== uid) throw new Error("Card not accessible.");

  await updateDoc(ref, {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

async function deleteCollectionByDeck(collectionName, uid, deckId) {
  const chunkSize = 400;
  while (true) {
    const snap = await getDocs(
      query(
        collection(db, collectionName),
        where("ownerUid", "==", uid),
        where("deckId", "==", deckId),
        limit(chunkSize)
      )
    );

    if (snap.empty) break;

    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
}

export async function deleteDeckCascade(deckId) {
  const uid = requireUid();
  const deckRef = doc(db, DECKS, deckId);
  const deckSnap = await getDoc(deckRef);
  if (!deckSnap.exists()) throw new Error("Deck not found.");
  if (deckSnap.data().ownerUid !== uid) throw new Error("Deck not accessible.");

  await deleteCollectionByDeck(CARDS, uid, deckId);
  await deleteCollectionByDeck(SESSIONS, uid, deckId);
  await deleteCollectionByDeck(REVIEWS, uid, deckId);
  await deleteDoc(deckRef);
}
